import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { BlogTitle } from "@/components/BlogTitle/BlogTitle.tsx";
import { Page } from "@/components/Page.tsx";
import ReactSvgGraphics from "@/components/toys/react-svg-graphics/ReactSvgGraphics.tsx";
import "@/styles/Blog.scss";
import DriveclearDemoV1 from "./driveclear-demo-v1.gif";
export const _frontmatter = {
  "title": "2D Graphics with React and SVG",
  "path": "/blog/2023/May/react-svg-graphics",
  "date": "2023-05-06T00:00:00.000Z",
  "description": "A rich 2D CAD graphics experience with React and SVG"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Page mdxType="Page">
      <BlogTitle title={props.pageContext.frontmatter.title} datePublished={props.pageContext.frontmatter.date} mdxType="BlogTitle" />
      <p>{`Off to the side, I've been building and maintaining my commercial engineering app
`}<a parentName="p" {...{
          "href": "https://driveclear.app"
        }}>{`Drive Clear`}</a>{` that helps engineers design vehicle driveways. A core part of
the experiences is a 2D Graphics view, which displays all the engineering geometry and annotations
(dimensions, labels, etc). This graphics display allows the user to zoom and pan within the view,
and provides a kind of "CAD like" experience.`}</p>
      <img src={DriveclearDemoV1} />
      <p>{`Back when I started the project, after some shopping around, I settled on PaperJS as the graphics
library. As a bonus, it also came with robust geometry clipping functions, for calculating
intersections and unions. This was sitting inside a React app, and updates to the PaperJS view was
done imperatively.`}</p>
      <p>{`At the time these were acceptable choices - they kept velocity high. But as time's gone on, it's
become more difficult to ship new features because of the vendor lock-in with
`}<a parentName="p" {...{
          "href": "http://paperjs.org/"
        }}>{`PaperJS`}</a>{`, and the imperative updates to the view.`}</p>
      <h2>{`The Ideal State`}</h2>
      <p>{`In an ideal world, being able to describe the geometry in a declarative way with React JSX like
syntax would be fantastic. This would allow for a more declarative approach to laying out the
graphics viewport elements like a document, `}<strong parentName="p">{`and`}</strong>{` drive the view from the state of the app and
whatever geometric calculations were needed. Something like:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-jsx"
        }}>{`<Viewport>
  <Vehicle position={new Point(0, 0)} rotation={12} />
  <Surface points={designSurfacePoints} stroke="blue" strokeWidth={2} />
</Viewport>
`}</code></pre>
      <p>{`Some more shopping later, I came across `}<a parentName="p" {...{
          "href": "https://github.com/alexbol99/flatten-js"
        }}>{`FlattenJS`}</a>{` a pure
calculation only library for 2D geometry. Great! This would decouple the geometric representation
from the view and allow for a more declarative approach to rendering elements.`}</p>
      <p>{`The final piece of the puzzle was rendering the view itself. It was a question between SVG and
Canvas. Canvas is a bit more performant, but SVG is more declarative. In the end I decided to go
with SVG to `}<em parentName="p">{`index`}</em>{` a little more on the declarative side, and allow hooking into "browser-native"
events like `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` and `}<inlineCode parentName="p">{`onMouseOver`}</inlineCode>{` of the SVG elements.`}</p>
      <h2>{`Initial Approach`}</h2>
      <p>{`Typically with 2D Graphics rendering, you have a world space coordinate system, and a view space
coordinate system. In world space coordinate, we would probably represent 1-unit as 1-meter, and
upwards in the y-axis is positive. In view space, `}<inlineCode parentName="p">{`{ x: 0, y: 0 }`}</inlineCode>{` is typically in the top-left
corner, and `}<em parentName="p">{`downwards`}</em>{` in the y-axis is positive.`}</p>
      <p>{`The initial approach was to use React context to hold the transformation data between world and view
space. The transformation data being an Affine Matrix, which is a 3x3 matrix that can represent
translation, rotation, and scaling.`}</p>
      <p>{`Then each geometric element is a just a React component consuming this context to return an SVG
element in view space. And.. it was half decent!`}</p>
      <ReactSvgGraphics mdxType="ReactSvgGraphics" />
      <p>{`The Grid itself is just a css background-image. The viewport and its elements are decleratively laid
out as;`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`<ViewportProvider
  id="viewport"
  baseScale={BaseScale}
  style={{ height: "20rem" }}
  header={<Header />}
>
  <SvgCircle center={new Point(0, 0)} radius={50} />
  <SvgCircle center={new Point(20, 0)} radius={40} />
  <SvgCircle center={new Point(-100, 20)} radius={25} />
</ViewportProvider>
`}</code></pre>
      <p>{`Super maintainable 🤙 -
`}<a parentName="p" {...{
          "href": "https://github.com/VivekRajagopal/vivek-rajagopal/blob/main/src/components/toys/react-svg-graphics/ReactSvgGraphics.tsx"
        }}>{`Get the source code`}</a></p>
      <h2>{`But.`}</h2>
      <p>{`It wasn't as performant as it could be 😞 - an issue I only discovered when I tested the rewrite on
an old laptop. Unfortunately, I couldn't compromise on the performance, especially since I had a
benchmark in the existing PaperJS imperative approach.`}</p>
      <p>{`Drive Clear will render a large number of polygons as part of the engineering design that it does.
Each polygon needs each of its points to be transformed from world space to view space... and each
pan event would trigger this transformation and re-render... and we can see where this is going.`}</p>
      <p>{`It was noticibly laggy when panning the view, which is a pretty critical action in the app 😬`}</p>
      <h2>{`The Optimisation`}</h2>
      <p>{`Well, if performance is struggling due to panning, then can we optimise for that? What if we offload
the transformation to the browser in the form of CSS transformations? This would take it out of the
React render cycle, which is optimised for reactivity and your standard HTML elements, not reactive
graphics really.`}</p>
      <p>{`To take it a step further, let's do `}<em parentName="p">{`this specific transformation`}</em>{` imperatively, out of the React
render logic. That should eliminate any React render cycle overhead and re-renders of child
components, and we're just doing synchronous DOM updates. To get it right, the transformation was
applied to a root `}<inlineCode parentName="p">{`<g>`}</inlineCode>{` element inside the `}<inlineCode parentName="p">{`<svg>`}</inlineCode>{` element, with all the geometry rendered inside
it. So the transformation from world space to view space happens only once on this root element. All
child elements are simply rendered in world space, and there's no need to listen to pan events and
re-render each time.`}</p>
      <p>{`The final DOM transformation looks a little like this;`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-ts"
        }}>{`const setTransform = (pan: Point, scale: number) => {
  if (!svgRef.current || !groupRef.current) return;

  const viewportSize = getViewportSize();
  const viewPan = pan.translate(new Point(viewportSize.x / 2, viewportSize.y / 2));

  const m = svgRef.current.createSVGMatrix();
  const tm = m.translate(viewPan.x, viewPan.y).scale(scale);

  const transform = groupRef.current!.transform.baseVal.createSVGTransformFromMatrix(tm);

  groupRef.current!.transform.baseVal.clear();
  groupRef.current!.transform.baseVal.appendItem(transform);

  const limitedScale = Math.min(maxZoom, Math.max(minZoom, scale));
  const displayGridSpacing = baseScale * (limitedScale / baseScale);

  svgRef.current.style.backgroundPosition = \`\${viewPan.x}px \${viewPan.y}px\`;
  svgRef.current.style.backgroundSize = \`\${displayGridSpacing}px \${displayGridSpacing}px\`;
};
`}</code></pre>
      <p>{`I ended up using RxJS to still get reactivity on pan and scale events, and trigger `}<inlineCode parentName="p">{`setTransform`}</inlineCode>{` in
the subscription.`}</p>
      <p>{`Yes it's a little ugly and goes against the declerative principle, but it's isolated to this one
instance and it's a vital performance optimisation. I'm happy to make the trade-off here.`}</p>
      <hr></hr>
      <p>{`The rewrite has been released 🚀 and it's working well. It's now much easier to lay out new
geometry, and with geometry being driven by the state of the app, it's much easier to reason about
which helps with features and bug fixes.`}</p>
      <p>{`But if I could redo this journey, I would be placing more emphasis on performance and functionality
parity with the existing PaperJS approach. I did do a POC before doing the rewrite, but I didn't
spend enough time testing it's performance with real use-cases seen in Drive Clear.`}</p>
      <p>{`I'm happy with the outcome though, and I'm looking forward to adding more features to the app 🤓`}</p>
    </Page>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      